import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface TourState {
  // Track which tours have been seen
  seenTours: Record<string, boolean>;
  // Current active tour
  activeTourId: string | null;
  // Tour open state
  isTourOpen: boolean;
}

interface TourActions {
  // Check if a specific tour has been seen
  hasSeenTour: (tourId: string) => boolean;
  // Mark a tour as seen
  markTourAsSeen: (tourId: string) => void;
  // Open a tour
  openTour: (tourId: string) => void;
  // Close current tour
  closeTour: () => void;
  // Reset all tour states (for testing/admin)
  resetTours: () => void;
}

type TourStore = TourState & TourActions;

export const useTourStore = create<TourStore>()(
  persist(
    (set, get) => ({
      // Initial state
      seenTours: {},
      activeTourId: null,
      isTourOpen: false,

      // Actions
      hasSeenTour: (tourId: string) => {
        return get().seenTours[tourId] || false;
      },

      markTourAsSeen: (tourId: string) => {
        set((state) => ({
          seenTours: {
            ...state.seenTours,
            [tourId]: true
          }
        }));
      },

      openTour: (tourId: string) => {
        set({
          activeTourId: tourId,
          isTourOpen: true
        });
      },

      closeTour: () => {
        const { activeTourId, markTourAsSeen } = get();
        if (activeTourId) {
          markTourAsSeen(activeTourId);
        }
        set({
          activeTourId: null,
          isTourOpen: false
        });
      },

      resetTours: () => {
        set({
          seenTours: {},
          activeTourId: null,
          isTourOpen: false
        });
      }
    }),
    {
      name: 'tour-storage', // localStorage key
      partialize: (state) => ({ seenTours: state.seenTours }) // Only persist seenTours
    }
  )
);